import React from 'react';
import { theme, getShadow, getColor, getSpacing } from '../theme';

const Card = ({
    children,
    className = '',
    variant = 'elevation',
    elevation = 'md',
    noPadding = false,
    hover = false,
    ...props
}) => {
    const baseClasses = 'bg-white rounded-lg overflow-hidden transition-shadow duration-200';

    const elevationClasses = {
        none: '',
        sm: 'shadow-sm hover:shadow-md',
        md: 'shadow-md hover:shadow-lg',
        lg: 'shadow-lg hover:shadow-xl',
        xl: 'shadow-xl hover:shadow-2xl'
    };

    const variantClasses = {
        elevation: hover ? elevationClasses[elevation] : elevationClasses[elevation].split(' ')[0],
        outlined: 'border border-gray-200 hover:border-gray-300'
    };

    const paddingClasses = noPadding ? '' : 'p-4';

    const combinedClasses = `
        ${baseClasses}
        ${variantClasses[variant]}
        ${paddingClasses}
        ${className}
    `.trim().replace(/\s+/g, ' ');

    return (
        <div
            className={combinedClasses}
            role="article"
            {...props}
        >
            {children}
        </div>
    );
};

const CardHeader = ({
    title,
    subheader,
    action,
    avatar,
    className = '',
    ...props
}) => {
    const baseClasses = 'px-6 py-4 border-b border-gray-200';

    return (
        <div
            className={`${baseClasses} ${className}`.trim()}
            {...props}
        >
            <div className="flex justify-between items-start">
                <div className="flex items-center">
                    {avatar && (
                        <div className="mr-4">
                            {avatar}
                        </div>
                    )}
                    <div>
                        {title && (
                            <h3 className="text-xl font-medium text-gray-900 leading-6">
                                {title}
                            </h3>
                        )}
                        {subheader && (
                            <p className="mt-1 text-sm text-gray-500">
                                {subheader}
                            </p>
                        )}
                    </div>
                </div>
                {action && (
                    <div className="ml-4 flex-shrink-0">
                        {action}
                    </div>
                )}
            </div>
        </div>
    );
};

const CardContent = ({
    children,
    className = '',
    ...props
}) => {
    const baseClasses = 'px-6 py-4 text-gray-700';

    return (
        <div
            className={`${baseClasses} ${className}`.trim()}
            {...props}
        >
            {children}
        </div>
    );
};

const CardActions = ({
    children,
    className = '',
    align = 'right',
    disableSpacing = false,
    ...props
}) => {
    const baseClasses = 'px-6 py-3 border-t border-gray-200';
    const alignmentClasses = {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end',
        between: 'justify-between'
    };
    const spacingClasses = disableSpacing ? '' : 'gap-2';

    return (
        <div
            className={`
                ${baseClasses} 
                flex items-center 
                ${alignmentClasses[align]} 
                ${spacingClasses}
                ${className}
            `.trim().replace(/\s+/g, ' ')}
            {...props}
        >
            {children}
        </div>
    );
};

const CardMedia = ({
    component: Component = 'div',
    image,
    alt = '',
    height,
    className = '',
    ...props
}) => {
    const baseClasses = 'w-full bg-cover bg-center bg-no-repeat';
    const style = {
        backgroundImage: `url(${image})`,
        height: height || '200px'
    };

    return (
        <Component
            className={`${baseClasses} ${className}`.trim()}
            style={style}
            role="img"
            aria-label={alt}
            {...props}
        />
    );
};

Card.Header = CardHeader;
Card.Content = CardContent;
Card.Actions = CardActions;
Card.Media = CardMedia;

export default Card;
